import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { 
    UserRound as UserRoundIcon, 
    Search as SearchIcon,
    ArrowRight as ArrowRightIcon,
    Briefcase,
    Users,
    TrendingUp,
    Award,
    Building2,
    BookOpen,
    Target,
    CheckCircle,
    Star,
    Globe,
    Zap,
    Shield,
    Clock,
    MessageSquare
} from 'lucide-react';
import Header from "../components/Header";
import Footer from "../components/Footer";

const Home = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);

    // Refs for scroll-triggered sections
    const statsRef = useRef(null);
    const featuresRef = useRef(null);
    const benefitsRef = useRef(null);
    const ctaRef = useRef(null);

    // In-view flags
    const [statsInView, setStatsInView] = useState(false);
    const [featuresInView, setFeaturesInView] = useState(false);
    const [benefitsInView, setBenefitsInView] = useState(false);
    const [ctaInView, setCtaInView] = useState(false);

    // Hero slideshow
    const heroImages = [
        "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1600&q=80",
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80",
        "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1600&q=80"
    ];
    const [heroIndex, setHeroIndex] = useState(0);

    // Animation trigger
    useEffect(() => {
        setIsVisible(true);
    }, []);

    // IntersectionObserver for sections
    useEffect(() => {
        const mkObserver = (cb) => new IntersectionObserver(
            (entries) => entries.forEach(e => e.isIntersecting && cb(true)),
            { root: null, rootMargin: "0px", threshold: 0.15 }
        );

        const statsObs = mkObserver(setStatsInView);
        const featObs = mkObserver(setFeaturesInView);
        const benObs = mkObserver(setBenefitsInView);
        const ctaObs = mkObserver(setCtaInView);

        statsRef.current && statsObs.observe(statsRef.current);
        featuresRef.current && featObs.observe(featuresRef.current);
        benefitsRef.current && benObs.observe(benefitsRef.current);
        ctaRef.current && ctaObs.observe(ctaRef.current);

        return () => {
            statsObs.disconnect();
            featObs.disconnect();
            benObs.disconnect();
            ctaObs.disconnect();
        };
    }, []);

    // Hero slideshow rotation
    useEffect(() => {
        const id = setInterval(() => {
            setHeroIndex((i) => (i + 1) % heroImages.length);
        }, 5000);
        return () => clearInterval(id);
    }, [heroImages.length]);

    // Redirect based on user role
    useEffect(() => {
        if (user) {
            switch (user.role) {
                case 'e2e-admin':
                    navigate('/admin-dashboard');
                    break;
                case 'recruiter':
                    navigate('/recruiter-dashboard');
                    break;
                case 'user':
                default:
                    // Stay on home page for regular users
                    break;
            }
        }
    }, [user, navigate]);

    const handleGetStarted = () => {
        if (user) {
            navigate("/profile");
        } else {
            navigate("/create-account");
        }
    };

    // Don't render the home page if user should be redirected
    if (user && (user.role === 'recruiter')) {
        return null;
    }

    const features = [
        {
            icon: SearchIcon,
            title: "Smart Job Search",
            description: "Advanced AI-powered job matching based on your skills and preferences.",
            image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80"
        },
        {
            icon: Users,
            title: "Talent Management",
            description: "Comprehensive tools for tracking and assessing candidates.",
            image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80"
        },
        {
            icon: Award,
            title: "Skills Assessment",
            description: "Interactive evaluations to showcase your technical abilities.",
            image: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=800"
        },
        {
            icon: Building2,
            title: "Company Profiles",
            description: "Explore company cultures and available opportunities.",
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80"
        },
        {
            icon: BookOpen,
            title: "Career Guidance",
            description: "Expert advice and resources for career growth.",
            image: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?auto=format&fit=crop&w=800&q=80"
        },
        {
            icon: TrendingUp,
            title: "Application Tracking",
            description: "Real-time updates on your job application status.",
            image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80"
        }
    ];

    const stats = [
        { number: "10K+", label: "Active Jobs", icon: Briefcase },
        { number: "5K+", label: "Companies", icon: Building2 },
        { number: "50K+", label: "Candidates", icon: Users },
        { number: "95%", label: "Success Rate", icon: TrendingUp }
    ];

    const benefits = [
        {
            icon: Zap,
            title: "Lightning Fast",
            description: "Get matched with jobs in seconds",
            image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=800&q=80"
        },
        {
            icon: Shield,
            title: "Secure & Private",
            description: "Enterprise-grade security for your data",
            image: "https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg?auto=compress&cs=tinysrgb&w=800"
        },
        {
            icon: Globe,
            title: "Global Reach",
            description: "Opportunities from companies worldwide",
            image: "https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&w=800&q=80"
        },
        {
            icon: Clock,
            title: "24/7 Support",
            description: "Round-the-clock assistance",
            image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
        }
    ];

    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 to-blue-100">
            <Header />
            
            {/* Hero Section */}
            <section className="relative overflow-hidden py-20 px-4">
                {/* Hero slideshow background */}
                <div className="absolute inset-0">
                    {heroImages.map((src, idx) => (
                        <img
                            key={src}
                            src={src}
                            alt="hero background"
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-out ${
                                heroIndex === idx ? 'opacity-100' : 'opacity-0'
                            }`}
                        />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/85 to-white/80" />
                </div>
                <div className={`relative w-full text-center transition-all duration-1000 transform ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                    <div className="mb-8">
                        <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent mb-6 leading-tight">
                            Your Career Journey
                            <br />
                            <span className="text-4xl md:text-6xl">Starts Here</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
                            Connect with innovative companies and unlock your potential worldwide.
                        </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
                        {!user && (
                        <button
                            onClick={handleGetStarted}
                            className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                        >
                            <span className="relative z-10 flex items-center">
                                Get Started Today
                                <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </button>
                        )}
                        
                        <Link
                            to="/job-search"
                            className="group px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-blue-600 hover:text-blue-600 transition-all duration-300 transform hover:scale-105 bg-white/80 backdrop-blur-sm"
                        >
                            <span className="flex items-center">
                                Explore Jobs
                                <SearchIcon className="ml-2 w-5 h-5 group-hover:rotate-12 transition-transform" />
                            </span>
                        </Link>
                    </div>

                    {/* Stats */}
                    <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full">
                        {stats.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div 
                                    key={index} 
                                    className={`text-center transform transition-all duration-700 ${
                                        statsInView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                    }`}
                                    style={{ transitionDelay: `${index * 100}ms` }}
                                >
                                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                                        <Icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                                        <div className="text-3xl font-bold text-gray-800 mb-1">{stat.number}</div>
                                        <div className="text-gray-600 font-medium">{stat.label}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section ref={featuresRef} className="py-20 px-4 bg-white">
                <div className="w-full">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                            Powerful Features for
                            <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent"> Every Need</span>
                        </h2>
                        <p className="text-xl text-gray-600">
                            From job matching to talent management, succeed with our tools.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div
                                    key={index}
                                    className={`group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-700 hover:-translate-y-2 border border-gray-100 hover:border-blue-200 transform ${
                                        featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                                    }`}
                                    style={{ transitionDelay: `${index * 120}ms` }}
                                >
                                    <img
                                        src={feature.image}
                                        alt={feature.title}
                                        className="w-full h-48 object-cover transition-opacity duration-500 group-hover:opacity-80"
                                    />
                                    <div className="p-6">
                                        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-4 mb-6 group-hover:scale-110 transition-transform duration-300">
                                            <Icon className="w-full h-full text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                                            {feature.title}
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed mb-4">
                                            {feature.description}
                                        </p>
                                        <Link
                                            to="/job-search"
                                            className="text-blue-600 font-medium hover:text-blue-700 flex items-center"
                                        >
                                            Learn More
                                            <ArrowRightIcon className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section ref={benefitsRef} className="py-20 px-4 bg-gradient-to-br from-blue-50 to-blue-100">
                <div className="w-full">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                            Why Choose Our Platform?
                        </h2>
                        <p className="text-xl text-gray-600">
                            Experience cutting-edge technology and dedicated support.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => {
                            const Icon = benefit.icon;
                            return (
                                <div key={index} className={`text-center group transform transition-all duration-700 ${benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${index * 120}ms` }}>
                                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                                        <img
                                            src={benefit.image}
                                            alt={benefit.title}
                                            className="w-full h-32 object-cover rounded-lg mb-6 transition-opacity duration-500 group-hover:opacity-80"
                                        />
                                        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-4 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                            <Icon className="w-full h-full text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-3">
                                            {benefit.title}
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            {benefit.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section ref={ctaRef} className="py-20 px-4 bg-gradient-to-br from-blue-800 to-blue-900">
                <div className={`w-full text-center text-white transform transition-all duration-700 ${ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-6">
                        Ready to Transform Your Career?
                    </h2>
                    <p className="text-xl mb-10 opacity-90">
                        Join thousands of professionals who have found their dream jobs.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        {!user && (
                        <button
                            onClick={handleGetStarted}
                            className="group px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl"
                        >
                            <span className="flex items-center justify-center">
                                Start Your Journey
                                <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </button>
                        )}
                        
                        {!user && (
                            <Link
                                to="/login"
                                className="group px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105"
                            >
                                <span className="flex items-center justify-center">
                                    Sign In
                                    <UserRoundIcon className="ml-2 w-5 h-5" />
                                </span>
                            </Link>
                        )}
                    </div>
                </div>
            </section>
            
            <Footer />
        </div>
    );
};

export default Home;